import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

const fmt = (n) => Math.round(n || 0).toLocaleString()
const fmtM = (n) => `${((n || 0) / 1000000).toFixed(1)}M`

const QuickAction = ({ icon, label, to, color = 'var(--green-deep)' }) => {
  const navigate = useNavigate()
  return (
    <button onClick={() => navigate(to)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 10px', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer', flex: 1, minWidth: 80, transition: 'all 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
      onMouseLeave={e => e.currentTarget.style.background = 'var(--cream)'}
    >
      <span style={{ fontSize: '1.3rem' }}>{icon}</span>
      <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
    </button>
  )
}

export default function DashboardFinance() {
  const { profile } = useAuth()
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })
    const monthStart = today.slice(0, 7) + '-01'

    const [jeRes, invoicesRes, payablesRes, approvalsRes, loansRes] = await Promise.all([
      supabase.from('journal_entries').select('debit_account,credit_account,amount,entry_date').gte('entry_date', monthStart),
      supabase.from('invoices').select('balance_due,status,invoice_date').in('status', ['Unpaid', 'Partial']),
      supabase.from('payables').select('amount_due,amount,due_date,status').eq('status', 'Unpaid'),
      supabase.from('requisitions').select('id,status,req_number').eq('status', 'Pending'),
      supabase.from('loans').select('outstanding_balance,interest_rate,lender_name'),
    ])

    const je = jeRes.data || []

    // Cash balance from all-time journals
    const { data: allJe } = await supabase.from('journal_entries').select('debit_account,credit_account,amount').eq('debit_account', '1101').or('credit_account.eq.1101')
    const cashIn = (allJe || []).filter(e => e.debit_account === '1101').reduce((s, e) => s + e.amount, 0)
    const cashOut = (allJe || []).filter(e => e.credit_account === '1101').reduce((s, e) => s + e.amount, 0)
    const cashBalance = cashIn - cashOut

    // Monthly revenue
    const monthRevenue = je.filter(e => e.credit_account?.startsWith('4')).reduce((s, e) => s + e.amount, 0)
    const monthCosts = je.filter(e => e.debit_account?.startsWith('5') || e.debit_account?.startsWith('6')).reduce((s, e) => s + e.amount, 0)
    const monthProfit = monthRevenue - monthCosts

    // Receivables
    const receivables = (invoicesRes.data || []).reduce((s, i) => s + (parseFloat(i.balance_due) || 0), 0)
    const overdueInvoices = (invoicesRes.data || []).filter(i => new Date(i.invoice_date) < new Date(Date.now() - 30 * 86400000)).length

    // Payables
    const payablesDue = (payablesRes.data || []).reduce((s, p) => s + (parseFloat(p.amount_due || p.amount) || 0), 0)
    const overduePayables = (payablesRes.data || []).filter(p => p.due_date && p.due_date < today).length

    // Pending approvals
    const pendingApprovals = (approvalsRes.data || []).length

    // Loan balance
    const totalLoans = (loansRes.data || []).reduce((s, l) => s + (parseFloat(l.outstanding_balance) || 0), 0)

    setData({ cashBalance, monthRevenue, monthCosts, monthProfit, receivables, overdueInvoices, payablesDue, overduePayables, pendingApprovals, totalLoans })
    setLoading(false)
  }

  const KPI = ({ label, value, sub, color = 'var(--text)', alert }) => (
    <div className="kpi" style={{ background: alert ? 'var(--red-pale)' : undefined, borderLeft: alert ? '3px solid var(--red)' : undefined }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ color, fontSize: '1.05rem' }}>{value}</div>
      {sub && <div className="kpi-sub" style={{ color: alert ? 'var(--red)' : undefined }}>{sub}</div>}
    </div>
  )

  return (
    <AppLayout title="Finance Dashboard">
      <div style={{ padding: '24px 24px 40px' }}>
        {/* Welcome */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.4rem', color: 'var(--green-deep)', marginBottom: 2 }}>
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {profile?.full_name?.split(' ')[0]} 👋
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Here's your financial overview for today.</p>
        </div>

        {/* KPI Strip */}
        {loading ? (
          <div className="kpi-grid" style={{ marginBottom: 24 }}>
            {[1,2,3,4,5,6].map(i => <div key={i} className="kpi" style={{ background: 'var(--cream)', height: 80 }} />)}
          </div>
        ) : (
          <div className="kpi-grid" style={{ marginBottom: 24 }}>
            <KPI label="Cash at Bank" value={`UGX ${fmtM(data.cashBalance)}`} color={data.cashBalance > 0 ? 'var(--green-deep)' : 'var(--red)'} />
            <KPI label="Receivables Outstanding" value={`UGX ${fmtM(data.receivables)}`} sub={data.overdueInvoices > 0 ? `${data.overdueInvoices} overdue` : 'all current'} alert={data.overdueInvoices > 0} color="var(--green-mid)" />
            <KPI label="Payables Due" value={`UGX ${fmtM(data.payablesDue)}`} sub={data.overduePayables > 0 ? `${data.overduePayables} overdue` : 'all current'} alert={data.overduePayables > 0} color="var(--red)" />
            <KPI label="Month Revenue" value={`UGX ${fmtM(data.monthRevenue)}`} color="var(--green-mid)" />
            <KPI label="Month Net P&L" value={`UGX ${fmtM(data.monthProfit)}`} color={data.monthProfit >= 0 ? 'var(--green-deep)' : 'var(--red)'} />
            <KPI label="Pending Approvals" value={data.pendingApprovals} alert={data.pendingApprovals > 0} sub={data.pendingApprovals > 0 ? 'need attention' : 'all clear'} color={data.pendingApprovals > 0 ? 'var(--red)' : 'var(--green-mid)'} />
          </div>
        )}

        {/* Quick Actions */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>⚡ Quick Actions</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { icon: '💵', label: 'Cash Log', to: '/cash-log' },
              { icon: '💸', label: 'Payables', to: '/payables' },
              { icon: '📄', label: 'Invoices', to: '/invoices' },
              { icon: '📊', label: 'Statements', to: '/financial-statements' },
              { icon: '📒', label: 'Journal Ledger', to: '/journal-ledger' },
              { icon: '🏦', label: 'Equity', to: '/equity' },
              { icon: '🏗️', label: 'Fixed Assets', to: '/fixed-assets' },
              { icon: '🔒', label: 'Period Close', to: '/period-close' },
            ].map(a => <QuickAction key={a.to} {...a} />)}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Approval Queue */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title">📋 Approval Queue</div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/requisitions')}>View All</button>
            </div>
            {data.pendingApprovals === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', padding: '16px 0', textAlign: 'center' }}>✅ No pending approvals</div>
            ) : (
              <div style={{ color: 'var(--amber)', fontSize: '0.82rem', padding: '12px', background: 'var(--amber-pale)', borderRadius: 6 }}>
                ⚠️ {data.pendingApprovals} requisition{data.pendingApprovals > 1 ? 's' : ''} awaiting approval
              </div>
            )}
          </div>

          {/* Loans Summary */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title">🏦 Loan Position</div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/loans')}>Manage</button>
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '1.1rem', fontWeight: 700, color: 'var(--red)' }}>
              UGX {fmtM(data.totalLoans)}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>Total outstanding loan balance</div>
          </div>

          {/* Navigate to statements */}
          <div className="card" style={{ gridColumn: '1 / -1', cursor: 'pointer' }} onClick={() => navigate('/financial-statements')}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div className="card-title">📊 Financial Statements</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>P&L · Balance Sheet · Cash Flow · Trial Balance</div>
              </div>
              <span style={{ fontSize: '1.5rem', color: 'var(--text-muted)' }}>→</span>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
